import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Tabs,
  Tab,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Paper
} from '@material-ui/core'
import { OpenInNew as OpenInNewIcon } from '@material-ui/icons'
import GroupsTab from './GroupsTab'
import ItemsTab from './ItemsTab'
import EstimateTab from './EstimateTab'
import ExportPreviewTab from './ExportPreviewTab'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const STATUS_CHIP_PROPS = {
  parsing: { color: 'default', label: 'Parsing' },
  parsed: { color: 'primary', label: 'Parsed' },
  review_needed: { color: 'secondary', label: 'Review Needed', style: { backgroundColor: '#f57c00', color: '#fff' } },
  parse_failed: { color: 'secondary', label: 'Parse Failed' },
  mapped: { color: 'primary', label: 'Mapped' }
}

const TAB_LABELS = ['Groups', 'Items', 'Estimate', 'Export Preview']

const BidResponseWorkspace = ({ projectId, packageId, url }) => {
  const [pkg, setPkg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState(0)
  const [itemsGroupFilter, setItemsGroupFilter] = useState('all')

  // Jump from the Groups tab to the Items tab pre-filtered by a specific group.
  const handleGroupClick = (groupId) => {
    setItemsGroupFilter(groupId || 'all')
    setTab(1)
  }

  const fetchPackage = useCallback(() => {
    setLoading(true)
    setError('')
    axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}`)
      .then(res => {
        setPkg(res.data)
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load bid package.')
        setLoading(false)
      })
  }, [packageId, url])

  useEffect(() => {
    if (!packageId) return
    fetchPackage()
  }, [packageId, fetchPackage])

  const handleExport = () => {
    window.location = url + '/api/bid-estimation/bid-packages/' + packageId + '/export'
  }

  const getStatusChipProps = (status) => {
    return STATUS_CHIP_PROPS[status] || { color: 'default', label: status || 'Unknown' }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Typography color="error">{error}</Typography>
      </div>
    )
  }

  const statusProps = getStatusChipProps(pkg && pkg.status)

  return (
    <div style={{ padding: 24 }}>
      <BidEstimationHeader
        backTo={projectId ? `/bid-estimation/${projectId}` : '/bid-estimation'}
        backLabel="Project"
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          ...(projectId ? [{ label: 'Project', to: `/bid-estimation/${projectId}` }] : []),
          { label: 'Bid Response' }
        ]}
        title={pkg ? pkg.name : `Bid Response ${packageId}`}
        statusChip={{
          label: statusProps.label,
          style: statusProps.style || { backgroundColor: '#455a64', color: '#fff' }
        }}
        actions={(
          <>
            {pkg && pkg.sourceFile && pkg.sourceFile.s3Url && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={pkg.sourceFile.s3Url}
                target="_blank"
                rel="noopener noreferrer"
                title={pkg.sourceFile.originalFilename || 'Original file'}
                style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
              >
                View Original
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleExport}
            >
              Export
            </Button>
          </>
        )}
      />

      {/* Tabs */}
      <Paper>
        <Tabs
          value={tab}
          onChange={(e, newVal) => setTab(newVal)}
          indicatorColor="primary"
          textColor="primary"
        >
          {TAB_LABELS.map((label, i) => (
            <Tab key={i} label={label} />
          ))}
        </Tabs>

        <div style={{ padding: 24 }}>
          {tab === 0 && (
            <GroupsTab
              groups={pkg ? pkg.groups : []}
              onGroupUpdate={fetchPackage}
              onGroupClick={handleGroupClick}
              url={url}
            />
          )}
          {tab === 1 && (
            <ItemsTab
              packageId={packageId}
              projectId={projectId}
              url={url}
              onItemUpdate={fetchPackage}
              initialGroupFilter={itemsGroupFilter}
              onGroupFilterChange={setItemsGroupFilter}
              sourceFile={pkg ? pkg.sourceFile : null}
            />
          )}
          {tab === 2 && <EstimateTab packageId={packageId} url={url} onUpdate={fetchPackage} />}
          {tab === 3 && <ExportPreviewTab packageId={packageId} url={url} />}
        </div>
      </Paper>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(BidResponseWorkspace)
